import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { UserCircle, Mail, Phone, Building2, Briefcase, BadgeInfo, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const Field = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-orange-600" />
    </div>
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value || '—'}</p>
    </div>
  </div>
);

const SelfServicePortal = () => {
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/portal/me/employee`)
      .then((r) => setEmp(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load your profile'))
      .finally(() => setLoading(false));
  }, []);

  const org = emp?.organizational_assignment || {};
  const personal = emp?.personal_data || {};

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto" data-testid="self-service-portal">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <UserCircle className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
            <p className="text-sm text-gray-500">Welcome, {user?.full_name || user?.username}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center py-16 text-gray-500" data-testid="portal-loading">
            <Loader2 className="w-8 h-8 animate-spin text-orange-600 mb-2" />Loading your profile…
          </div>
        ) : error ? (
          <Card className="p-8 text-center" data-testid="portal-no-link">
            <BadgeInfo className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-gray-800">No linked employee record</h2>
            <p className="text-sm text-gray-500 mt-1">{error}</p>
          </Card>
        ) : (
          <Card className="p-6" data-testid="portal-profile">
            <div className="flex items-center gap-4 pb-5 border-b">
              <div className="w-16 h-16 rounded-full bg-orange-600 text-white flex items-center justify-center text-xl font-bold">
                {(emp.first_name || '?')[0]}{(emp.last_name || '')[0]}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{emp.first_name} {emp.last_name}</h2>
                <p className="text-sm text-gray-500">{emp.employee_id} · {org.position || org.job || 'Employee'}</p>
                <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">{emp.status}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-5">
              <Field icon={Mail} label="Email" value={personal.email || emp.email} />
              <Field icon={Phone} label="Mobile" value={personal.mobile || personal.phone} />
              <Field icon={Building2} label="Company Code" value={org.company_code} />
              <Field icon={Briefcase} label="Organizational Unit" value={org.organizational_unit} />
              <Field icon={BadgeInfo} label="Employee Group" value={org.employee_group} />
              <Field icon={UserCircle} label="Date of Joining" value={emp.date_of_joining || org.hire_date} />
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default SelfServicePortal;
